import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import AuthLayout from '../components/AuthLayout';
import AuthField from '../components/AuthField';
import FormError from '../components/FormError';
import InstallApp from '../components/InstallApp';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const invite = new URLSearchParams(location.search).get('invite');
  const { login } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setError('');
    try {
      await login(formData.email.trim(), formData.password);
      navigate(invite ? `/invite?token=${encodeURIComponent(invite)}` : '/');
    } catch (err) {
      setError(!err.response
        ? 'Cannot reach the server. Please check your connection and try again.'
        : err.response.status === 401
          ? 'Invalid email or password'
          : err.response.status >= 500
            ? 'Sign-in is temporarily unavailable. Please try again shortly.'
            : err.response.data?.error || 'Could not sign in. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title="Welcome back" description={invite ? 'Sign in to accept your team invitation.' : 'Sign in to pick up your conversations and follow-ups.'}>
      <form onSubmit={handleSubmit} className="space-y-5" aria-busy={loading}>
        <FormError message={error} />
        <AuthField id="login-email" label="Email" type="email" autoComplete="username" autoCapitalize="none" spellCheck={false}
          value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          placeholder="you@example.com" required disabled={loading} />
        <div>
          <AuthField id="login-password" label="Password" type={showPassword ? 'text' : 'password'} autoComplete="current-password"
            value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            showPassword={showPassword} onTogglePassword={() => setShowPassword(!showPassword)} required disabled={loading} />
          <div className="mt-1 flex justify-end">
            <Link to="/forgot-password" className="inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-700">Forgot password?</Link>
          </div>
        </div>
        <button data-testid="login-submit" type="submit" disabled={loading}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary-600 p-3 text-sm font-semibold text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60">
          {loading && <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />}
          <span>{loading ? 'Signing in...' : 'Sign In'}</span>
        </button>
      </form>
      <p role="status" className="sr-only">{loading ? 'Signing in...' : ''}</p>

      <p className="mt-6 border-t border-gray-200 pt-4 text-center text-sm text-gray-600">
        New to LeadMarka?{' '}
        <Link to={invite ? `/register?invite=${encodeURIComponent(invite)}` : '/register'} className="inline-flex items-center font-semibold text-primary-600 hover:text-primary-700">Create an account</Link>
      </p>
      <InstallApp className="mt-3" />
    </AuthLayout>
  );
};

export default Login;
